import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { Menu } from '@headlessui/react';
import { format } from 'date-fns';

import { changePageInfo } from '../../../../state/store/reducers/ui/headerSlice';
import { loadMembers, resetMembers, selectMembers, updateMemberRecord } from '../../../../state/store/reducers/entities/membersSlice';
import useToast from '../../../../state/hooks/useToast';
import { ListLoader } from '../../../../components/loaders';
import { Button, ErrorLoading, ListNoData, Pagination } from '../../../../components/common';
import { paginationInfo } from '../../../../utils/pagination';
import PopoverWithFloat from '../../../../components/hoc/PopoverWithFloat';
// import memberService from '../../../../services/api/paro/membersService';
import useAlert from '../../../../state/hooks/useAlert';
import DeviceListFilters from './components/DeviceListFilters';
import { SelectInput } from '../../../../components/form';
import { debounce } from 'lodash';
import schoolService from '../../../../services/api/unified-api/schoolService';


function DeviceListPage() {
    const dispatch = useDispatch();
    const members = useSelector(selectMembers);
    const [searchParams, setSearchParams] = useSearchParams();
    const [schoolListData, setSchoolListData] = useState({ loading: false, list: [] as any[] });

    const [memberForm, setMemberForm] = useState({ open: false, member: null });
    const [memberDetails, setMemberDetails] = useState({ open: false, member: null });

    const { addToast } = useToast();
    const { openAlertConfirm } = useAlert();

    // get query params
    const page = Number(searchParams.get('page')) || members.pager.page;
    const searchFilters = {
        name: searchParams.get('name') || '',
        email: searchParams.get('email') || '',
        primaryContact: searchParams.get('primaryContact') || '',
        connectionStatus: searchParams.get('connectionStatus') || '',
        schoolId: searchParams.get('schoolId') || '',
    };

    useEffect(() => {
        dispatch(changePageInfo({ title: "Devices" }));

        return () => {
            dispatch(resetMembers({}));
        }
    }, []);


    useEffect(() => {
        getMembers();
    }, [searchParams, dispatch]);



    const getMembers = async () => {
        await dispatch<any>(loadMembers({
            page: page,
            filters: searchFilters,
        }));
    };

    const getSchools = debounce(async (searchQuery: string) => {
        setSchoolListData({ loading: true, list: [] });
        const result = await schoolService.getSchools({ name: searchQuery });
        if (result.success) {
            setSchoolListData({ loading: false, list: result.data });
        } else {
            setSchoolListData({ loading: false, list: [] });
        }
    }, 500);

    const changeSchool = (schoolId: number | string) => {
        setSearchParams(current => ({
            schoolId: schoolId.toString(),
        }));
    }




    return (
        <div className=''>
            <div className='flex flex-wrap items-start justify-between mb-5'>
                <div className='flex-grow flex flex-col sm:flex-row flex-wrap gap-5'>
                    <div className='min-w-[14rem] max-w-[18rem]'>
                        <SelectInput
                            items={schoolListData.list}
                            labelKey='name'
                            valueKey='id'
                            searchable={true}
                            placeholder="Select school"
                            value={searchFilters["schoolId"]}
                            onChange={(school: any) => changeSchool(school.id)}
                            onSearch={(query) => getSchools(query)}
                            searching={schoolListData.loading}
                        />
                        <SelectInput
                            items={[
                                { id: 'connected', name: 'Connected' },
                                { id: 'disconnected', name: 'Disconnected' },
                            ]}
                            labelKey='name'
                            valueKey='id'
                            searchable={false}
                            placeholder="Select status"
                            value={searchFilters["connectionStatus"]}
                            onChange={(value: { name: string, id: string }) => {
                                setSearchParams(current => ({
                                    ...Object.fromEntries(current.entries()),
                                    connectionStatus: value.id,
                                    page: '1'
                                }));
                            }}
                            searching={members.loading}
                        />
                    </div>

                    <div className='border-b sm:border-b-0 sm:border-r border-gray-200 dark:border-gray-700'></div>

                    <DeviceListFilters
                        initialData={searchFilters}
                        onSearch={(values) => {
                            //setSearchParams({ ...values });
                            setSearchParams(current => {
                                const entries = { ...Object.fromEntries(current.entries()), ...values };
                                if (entries.page) {
                                    entries.page = '1';
                                }
                                return entries;
                            });
                        }}
                        searching={members.loading}
                    />
                </div>
            </div>


            {
                // if there're members
                !members.loadingError && !members.loading && members.list.length > 0 &&
                <React.Fragment>
                    <div className='max-w-full overflow-x-auto mb-6'>

                        <table className="border-collapse table-auto w-full">
                            <thead>
                                <tr className='text-left border-b-2 dark:border-gray-700'>
                                    <th className='py-6 px-6'>Name</th>
                                    <th className='py-6 px-6'>Email</th>
                                    <th className='py-6 px-6'>Mobile Number</th>
                                    <th className='py-6 px-6'>MemberType</th>
                                    <th className='py-6 px-6'>Status</th>
                                    <th className='py-6 px-6'>Last Connected</th>
                                    <th className='py-6 px-6'>Last Disconnected</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y dark:divide-gray-700/60">
                                {
                                    members.list.map((memberItem: any) =>
                                        <tr key={memberItem.id}>
                                            <td className='py-4 px-6'>
                                                <button type='button' onClick={() => setMemberDetails({ open: true, member: memberItem })} className='transition-colors duration-300 hocus:text-blue'>
                                                    {memberItem.name}
                                                </button>
                                            </td>
                                            <td className='py-4 px-6'>{memberItem.email}</td>
                                            <td className='py-4 px-6'>{memberItem.primaryContact}</td>
                                            <td className='py-4 px-6'>{memberItem.memberType}</td>
                                            <td className='py-4 px-6'>
                                                <span className={`inline-block font-medium text-sm uppercase ${memberItem.connectionStatus === "connected" ? "bg-green-500/10 text-green-500" : "bg-red-500/10 text-red-500"} rounded-lg py-1 px-3`}>{memberItem.connectionStatus?.toUpperCase()}</span>
                                            </td>
                                            <td className='py-4 px-6 whitespace-nowrap'>{memberItem.lastConnectedAt ? format(new Date(memberItem.lastConnectedAt), 'd LLL yyyy h:mm a') : 'N/A'}</td>
                                            <td className='py-4 px-6 whitespace-nowrap'>{memberItem.disconnectedAt ? format(new Date(memberItem.disconnectedAt), 'd LLL yyyy h:mm a') : 'N/A'}</td>
                                            <td className='py-4 px-6 text-right'>
                                                <Menu as="div" className="relative inline-block text-left">
                                                    <PopoverWithFloat placement='bottom-end' portal>
                                                        <Menu.Button type='button' className={`w-9 h-9 transition-colors duration-300 bg-opacity-0 rounded-full hover:bg-black/5 dark:hover:bg-white/5 ui-open:bg-black/5 dark:ui-open:bg-white/5`}>
                                                            <i className="ri-more-2-fill"></i>
                                                        </Menu.Button>

                                                        <Menu.Items as='div' unmount={false} className="w-64 right-0 origin-top-right rounded-xl bg-white dark:bg-gray-900 shadow-xl dark:shadow-white/5 overflow-hidden px-2 py-3">
                                                            <Menu.Item as="button" type='button' onClick={() => setMemberDetails({ open: true, member: memberItem })} className="w-full text-left hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg px-4 py-3">
                                                                View Details
                                                            </Menu.Item>
                                                            <Menu.Item as="button" type='button' onClick={() => setMemberForm({ open: true, member: memberItem })} className="w-full text-left hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg px-4 py-3">
                                                                Edit
                                                            </Menu.Item>

                                                        </Menu.Items>
                                                    </PopoverWithFloat>
                                                </Menu>
                                            </td>
                                        </tr>
                                    )
                                }
                            </tbody>
                        </table>
                    </div>

                    <div className='flex justify-between flex-wrap'>
                        <p className='mb-3 mr-8 text-gray-500 dark:text-gray-400'>{paginationInfo(members.pager.pageSize, members.pager.totalCount, page)}</p>

                        <Pagination
                            currentPage={page}
                            pageSize={members.pager.pageSize}
                            totalCount={members.pager.totalCount}
                            onPageChange={(page) => {
                                setSearchParams(current => ({
                                    ...Object.fromEntries(current.entries()),
                                    page: page.toString(),
                                }));
                            }}
                        />
                    </div>
                </React.Fragment>
            }

            {
                // if members are loading
                !members.loadingError && members.loading &&
                <ListLoader loadingText='Loading Devices' className='px-8 py-24' />
            }

            {
                // if there're no members in the app
                !members.loadingError && !members.loading && members.list.length === 0 && Object.keys(searchFilters).length === 0 &&
                <ListNoData title='No Devices Found' description='There are no Devices Added' className='px-8 py-24'>
                    {/* <Button type='button' onClick={() => setMemberForm({ open: true, member: null })}>Add a Member Here</Button> */}
                </ListNoData>
            }

            {
                // if there're no members in the app
                !members.loadingError && !members.loading && members.list.length === 0 && Object.keys(searchFilters).length > 0 &&
                <ListNoData title='No Devices Found' description='No devices were found for the selected scenarios' className='px-8 py-24' />
            }


            {
                members.loadingError &&
                <ErrorLoading title='Error Loading Devices' message='There was a problem loading devices' className='px-8 py-24' onTryAgain={getMembers} />
            }



        </div>
    );
}


export default DeviceListPage;